import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Info, Home, Building2, CheckCircle2, RotateCcw, ChevronDown, Gift } from 'lucide-react'
import PageContainer from '@/components/layout/PageContainer'
import StickySideBanner from '@/components/common/StickySideBanner'
import Button from '@/components/ui/Button'
import Card from '@/components/ui/Card'
import Spinner from '@/components/ui/Spinner'
import EmptyState from '@/components/common/EmptyState'
import SEO from '@/components/common/SEO'
import { useCareCalculatorStore } from '@/stores/careCalculatorStore'
import BenefitTierBadge from '@/components/care-calculator/BenefitTierBadge'
import BudgetFacilityList from '@/components/care-calculator/BudgetFacilityList'
import {
  estimateCareBenefit,
  formatRange,
  type EstimateResponse,
  type CostBlock,
  type BenefitGroup,
} from '@/features/carecalc/api'
import { useCalcQuestions } from '@/features/carecalc/api'
import { reductionTypeFromAnswers } from '@/features/carecalc/reduction'
import { trackCompleteCareCalculator } from '@/lib/analytics'

/** 비급여 출처별 안내 문구 */
const NON_COVERED_SOURCE_NOTE: Record<string, string> = {
  FACILITY: '이 시설이 실제로 신고한 식대·상급침실료 기준이에요.',
  REGION_AVG: '주변 요양원들이 실제로 신고한 금액의 평균이에요.',
  TYPE_DEFAULT: '이 지역은 신고 금액이 아직 적어서 전국 평균으로 계산했어요.',
}

export default function CareCalculatorResultPage() {
  const navigate = useNavigate()
  const { mode, answers, location, reset } = useCareCalculatorStore()
  const { data: questions } = useCalcQuestions(mode)

  const [result, setResult] = useState<EstimateResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  /** 완료 이벤트는 한 번만. effect 가 다시 돌아도 GA 수치가 부풀지 않게 한다 */
  const completeReported = useRef(false)

  useEffect(() => {
    if (!questions) return
    if (Object.keys(answers).length === 0) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    estimateCareBenefit({
      rulesetVersion: questions.rulesetVersion,
      answers,
      mode,
      lat: location?.latitude,
      lng: location?.longitude,
    })
      .then((r) => {
        if (cancelled) return
        setResult(r)
        // 계산이 실제로 성공했을 때만 완료로 본다. 화면 진입만으로는 완료가 아니다.
        // 답변은 건강정보라 보내지 않고, 집계용 값만 보낸다.
        if (completeReported.current) return
        completeReported.current = true
        trackCompleteCareCalculator({
          mode,
          band: r.band,
          answeredCount: r.answeredCount,
          unknownCount: r.unknownCount,
        })
      })
      .catch(() => {
        if (!cancelled) setError('금액을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [questions, answers, mode, location])

  const restart = () => {
    reset()
    navigate('/care-calculator')
  }

  /**
   * 모든 분기에서 같은 태그가 그려져야 한다.
   *
   * 크롤러는 계산이 끝나기 전 화면(로딩)을 보게 되는데, 그 분기에 태그가 없으면
   * 홈 타이틀을 상속받고 noindex 도 빠진다. 개인화 결과 화면이 그대로 색인되면
   * 내용 없는 페이지가 늘어난다.
   */
  const seo = (
    <SEO
      title="돌봄지원 계산 결과"
      description="예상 장기요양등급 구간과 집에서·요양원에 모실 때의 한 달 예상 비용입니다."
      url="/care-calculator/result"
      noindex
    />
  )

  if (loading) {
    return (
      <>
        {seo}
        <PageContainer>
          <div className="flex justify-center py-20">
            <Spinner size="lg" />
          </div>
        </PageContainer>
      </>
    )
  }

  if (Object.keys(answers).length === 0) {
    return (
      <>
        {seo}
        <PageContainer>
          <EmptyState
            message="아직 답하신 내용이 없어요"
            description="5분이면 예상 등급과 한 달 비용을 알려드려요"
            actionLabel="계산 시작하기"
            onAction={() => navigate('/care-calculator')}
          />
        </PageContainer>
      </>
    )
  }

  if (error || !result) {
    return (
      <>
        {seo}
        <PageContainer>
          <div className="flex flex-col items-center gap-4 py-16">
            <p className="text-body text-cb-gray-600">{error}</p>
            <Button onClick={() => window.location.reload()}>다시 시도</Button>
          </div>
        </PageContainer>
      </>
    )
  }

  return (
    <>
      {/* 좌우 세로배너 160x600 — 이 화면은 콘텐츠가 max-w-5xl 이라 2xl 이상에서만 자리가 난다.
          (512+160+48(여백) = 720) */}
      <StickySideBanner slotKey="carecalc_side_left" side="left" topPx={96} edgeOffsetPx={720} breakpoint="2xl" />
      <StickySideBanner slotKey="carecalc_side_right" side="right" topPx={96} edgeOffsetPx={720} breakpoint="2xl" />
      {seo}

      {/* 면책 — 스크롤과 무관하게 고정 */}
      <div className="sticky top-14 z-10 border-b border-cb-gray-100 bg-white/95 px-[var(--spacing-page-x)] py-2.5 backdrop-blur">
        <div className="mx-auto flex max-w-5xl gap-2">
          <Info size={14} className="mt-0.5 flex-shrink-0 text-cb-gray-500" />
          <p className="text-caption leading-relaxed text-cb-gray-600">{result.disclaimer}</p>
        </div>
      </div>

      {/* PageContainer 기본 상한은 480px(모바일 최적화)이라 PC 2단을 담지 못한다 */}
      <PageContainer noMaxWidth>
        <div className="mx-auto flex max-w-5xl flex-col gap-5 py-4 lg:py-8">
          {/* 밴드 */}
          <div>
            <h1 className="text-h2 font-bold text-cb-gray-900">{result.bandLabel}</h1>
            {result.bandDescription && (
              <p className="mt-1.5 text-body text-cb-gray-600">{result.bandDescription}</p>
            )}
            {result.borderline && (
              <p className="mt-2 text-body-sm text-cb-gray-500">
                한 등급 위로 나올 수도 있어서 금액을 넉넉하게 잡았어요.
              </p>
            )}
            {result.unknownCount > 0 && (
              <p className="mt-1 text-body-sm text-cb-gray-500">
                {result.unknownCount}가지를 모르겠다고 하셔서 폭이 조금 넓어요.
              </p>
            )}
          </div>

          {/* ★ 금액이 주인공 */}
          <Card>
            <p className="mb-4 text-body font-semibold text-cb-gray-600">
              한 달에 이만큼 드실 것 같아요
            </p>

            {/* 두 선택지는 나란히 놓아야 비교가 된다. 세로로 쌓으면 스크롤로 비교하게 된다 */}
            <div className="grid gap-4 divide-y divide-cb-gray-100 sm:grid-cols-2 sm:gap-6 sm:divide-x sm:divide-y-0">
              <CostRow
                icon={<Home size={18} className="text-cb-primary" />}
                label={
                  result.home.serviceType === 'DAY_CARE'
                    ? '집에서 (주야간보호)'
                    : '집에서 (방문요양)'
                }
                block={result.home}
              />

              <div className="pt-4 sm:pl-6 sm:pt-0">
                <CostRow
                  icon={<Building2 size={18} className="text-cb-primary" />}
                  label="요양원에 모시면"
                  block={result.facility}
                  detail={
                    result.facility.available
                      ? `급여 본인부담 ${formatRange(result.facility.copayLow, result.facility.copayHigh)} + 식대·상급침실료 등 ${formatRange(result.facility.nonCoveredLow, result.facility.nonCoveredHigh)}`
                      : undefined
                  }
                />
              </div>
            </div>

            {result.facility.available && result.facility.nonCoveredSource && (
              <p className="mt-3 flex gap-1.5 text-caption leading-relaxed text-cb-gray-600">
                <Info size={13} className="mt-0.5 flex-shrink-0" />
                {NON_COVERED_SOURCE_NOTE[result.facility.nonCoveredSource]}
              </p>
            )}

            <p className="mt-2 text-caption text-cb-gray-500">
              {result.rateYear}년 1월 1일 기준 공단 고시 금액으로 계산했어요. 실제 이용하신
              만큼만 내시므로 적게 쓰시면 금액도 줄어요.
            </p>
          </Card>

          {result.homeCareNote && (
            <div className="rounded-xl border border-cb-gray-200 bg-white p-4">
              <p className="text-body-sm leading-relaxed text-cb-gray-700">
                {result.homeCareNote}
              </p>
            </div>
          )}

          {/* 아래는 PC 2단 — 부피가 큰 지원 목록이 본문, 요약은 우측에 고정 */}
          <div className="flex flex-col gap-5 lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-start lg:gap-8">
            {/* 모바일은 신청 방법·근거를 먼저 읽고 목록으로 내려간다.
                PC 는 부피가 큰 목록이 왼쪽 본문이라 순서를 뒤집는다 */}
            <div className="order-2 flex flex-col gap-5 lg:order-1">
              {/* 받을 수 있는 지원 — 「돌봄지원 계산기」의 이름값 */}
              {result.benefitGroups.length > 0 && (
                <div>
                  <div className="mb-1 flex items-center gap-2">
                    <Gift size={18} className="text-cb-primary" />
                    <h2 className="text-h3 font-bold text-cb-gray-900">
                      받으실 수 있는 지원 {result.benefitCount}가지
                    </h2>
                  </div>
                  <p className="mb-3 text-body-sm text-cb-gray-600">
                    위 금액이 어디서 나왔는지, 그리고 그 밖에 어떤 도움을 받으실 수 있는지예요.
                  </p>

                  <div className="flex flex-col gap-2">
                    {result.benefitGroups.map((group) => (
                      <BenefitGroupCard key={group.domain} group={group} />
                    ))}
                  </div>

                  <p className="mt-3 flex gap-1.5 text-caption leading-relaxed text-cb-gray-600">
                    <Info size={13} className="mt-0.5 flex-shrink-0" />
                    위 금액은 공단이 정한 금액과 시설이 신고한 금액만 더한 거예요.
                    「조건 확인 필요」·「지역마다 달라요」 항목은 빠져 있어요.
                  </p>
                </div>
              )}

              {/* 시설 계산이 가능한 경우에만 — 인지지원등급은 요양원 대상이 아니다 */}
              {result.facility.available && (
                <BudgetFacilityList
                  band={result.band}
                  budgetMax={result.facility.totalHigh}
                  // 예산이 감경을 반영한 금액이라 시설도 같은 감경 요율로 걸러야 한다
                  reductionType={reductionTypeFromAnswers(answers)}
                  location={location}
                />
              )}
            </div>

            <aside className="order-1 flex flex-col gap-5 lg:order-2 lg:sticky lg:top-28">
              {/* 확정 정보 — 판정은 흐리게, 행동은 선명하게 */}
              <Card>
                <div className="mb-3 flex items-center gap-2">
                  <CheckCircle2 size={18} className="text-cb-secondary" />
                  <h2 className="text-body font-bold text-cb-gray-900">지금 하실 수 있는 것</h2>
                </div>
                <ul className="flex flex-col gap-2">
                  {result.confirmedFacts.map((fact) => (
                    <li
                      key={fact}
                      className="flex gap-2 text-body-sm leading-relaxed text-cb-gray-700"
                    >
                      <span className="text-cb-primary">·</span>
                      {fact}
                    </li>
                  ))}
                </ul>
              </Card>

              {/* 근거 */}
              {Object.keys(result.domainScores).length > 0 && (
                <Card>
                  <h2 className="mb-3 text-body font-bold text-cb-gray-900">이렇게 답하셨어요</h2>
                  <div className="flex flex-col gap-2.5">
                    {Object.entries(result.domainScores).map(([label, score]) => (
                      <div key={label} className="flex items-center gap-3">
                        <span className="w-20 flex-shrink-0 text-body-sm text-cb-gray-600">
                          {label}
                        </span>
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-cb-gray-100">
                          <div
                            className="h-full rounded-full bg-cb-primary transition-all"
                            style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
                          />
                        </div>
                        <span className="w-16 flex-shrink-0 text-right text-caption text-cb-gray-600">
                          {needLabel(score)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-caption leading-relaxed text-cb-gray-600">
                    실제 판정은 공단 조사원이 직접 방문해 살펴본 뒤 정해집니다. 저희는
                    {result.answeredCount}가지를 여쭤본 결과라 한 등급 정도는 다를 수 있어요.
                  </p>
                </Card>
              )}
            </aside>
          </div>

          <div className="flex flex-col gap-2 lg:mx-auto lg:w-64">
            <Button size="lg" variant="secondary" fullWidth onClick={restart}>
              <RotateCcw size={16} className="mr-1.5" />
              다시 계산하기
            </Button>
          </div>
        </div>
      </PageContainer>
    </>
  )
}

/**
 * 영역 점수를 말로 바꾼다.
 *
 * <p>숫자를 그대로 보여주면 공단 인정점수로 오해한다. 영역별 100점 환산은
 * 공단과 같은 단계까지의 중간값일 뿐이다 (영구지침 §1).</p>
 */
function needLabel(score: number): string {
  if (score >= 75) return '아주 많이'
  if (score >= 50) return '많이'
  if (score >= 25) return '조금'
  return '거의 없음'
}

/** 재가/시설 금액 한 줄 */
function CostRow({
  icon,
  label,
  block,
  detail,
}: {
  icon: React.ReactNode
  label: string
  block: CostBlock
  detail?: string
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5">
        {icon}
        <span className="text-body-sm font-medium text-cb-gray-600">{label}</span>
      </div>
      {block.available ? (
        <>
          <p className="text-[2rem] font-bold leading-tight text-cb-primary text-amount">
            {formatRange(block.totalLow, block.totalHigh)}
          </p>
          {detail && <p className="mt-1 text-caption text-cb-gray-600">{detail}</p>}
        </>
      ) : (
        <p className="text-body text-cb-gray-500">{block.unavailableReason}</p>
      )}
    </div>
  )
}

/** 영역별 지원 묶음 — 기본 접힘, 첫 그룹만 펼침 */
function BenefitGroupCard({ group }: { group: BenefitGroup }) {
  const [open, setOpen] = useState(group.domain === 'LONG_TERM_CARE')

  return (
    <div className="overflow-hidden rounded-xl border border-cb-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex min-h-[3.5rem] w-full items-center justify-between px-4 py-3 text-left"
      >
        <span className="text-body font-semibold text-cb-gray-900">
          {group.domainLabel}
          <span className="ml-2 text-body-sm font-normal text-cb-gray-500">
            {group.items.length}가지
          </span>
        </span>
        <ChevronDown
          size={18}
          className={`flex-shrink-0 text-cb-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <ul className="flex flex-col gap-3 border-t border-cb-gray-100 px-4 py-3">
          {group.items.map((item) => (
            <li key={item.code}>
              <div>
                <p className="text-body font-bold text-cb-gray-900">{item.title}</p>
                <p className="mt-1 text-body-sm leading-relaxed text-cb-gray-600">
                  {item.summary}
                </p>
                {/* 금액과 신뢰도를 나란히 — 금액이 얼마나 확실한지 바로 읽히게 */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <BenefitTierBadge tier={item.tier} />
                  {item.amount && (
                    // 항목명보다 작게, 주황 계열로 눈에 띄게.
                    // amber-700 은 흰 배경 대비 약 5.9:1 로 4.5:1 기준을 넘는다.
                    <span className="text-[0.8125rem] font-semibold text-amber-700">
                      {item.amount}
                    </span>
                  )}
                </div>
                {item.note && (
                  <p className="mt-1.5 text-body-sm leading-relaxed text-cb-gray-500">
                    {item.note}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
